import React from "react";
import { Box, Typography } from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import dayjs from "dayjs";
import {
  adjustTimes,
  getStopDepartureDatetime,
  getStopArrivalDatetime,
  getSegmentDurationMinutes,
} from "../../utils/DateTime";
import { getCityDisplay, cityMatchesSearch } from "../../utils/city";
import MapAddressLink from "../../components/MapAddressLink";
import { getRuntimeBRAND } from "../../brand/runtime";

export function encodeBase64Unicode(str) {
  try {
    return btoa(unescape(encodeURIComponent(str)));
  } catch (e) {
    return "";
  }
}

export function decodeBase64Unicode(str) {
  try {
    return decodeURIComponent(escape(atob(str)));
  } catch (e) {
    return "";
  }
}

export const getLegStopsSlice = (leg) => {
  if (!leg?.trip) return null;
  const legStops = adjustTimes(dayjs(leg.trip.departure_date), leg.trip.route?.stops ?? []);
  const fromIdx = legStops.findIndex((s) => cityMatchesSearch(s.city, leg.from_city));
  const toIdx = legStops.findIndex((s) => cityMatchesSearch(s.city, leg.to_city));
  if (fromIdx === -1 || toIdx === -1 || fromIdx >= toIdx) return null;
  return { fromIdx, toIdx, slice: legStops.slice(fromIdx, toIdx + 1) };
};

export const getLegSegment = (leg) => {
  if (!leg?.trip) return null;
  const stops = adjustTimes(dayjs(leg.trip.departure_date), leg.trip.route?.stops ?? []);
  const fromIdx = stops.findIndex((s) => cityMatchesSearch(s.city, leg.from_city));
  const toIdx = stops.findIndex((s) => cityMatchesSearch(s.city, leg.to_city));
  if (fromIdx === -1 || toIdx === -1 || fromIdx >= toIdx) return null;
  const fromStop = stops[fromIdx];
  const toStop = stops[toIdx];
  if (!getStopDepartureDatetime(fromStop) || !getStopArrivalDatetime(toStop)) return null;
  return { fromStop, toStop };
};

/** Підсумок напрямку (прямий або з пересадкою) для RT-карток і сайдбару. */
export const getDirectionSummary = (sel) => {
  if (!sel) return null;

  if (sel.itinerary?.legs?.length) {
    const legs = sel.itinerary.legs;
    const first = getLegSegment(legs[0]);
    const last = getLegSegment(legs[legs.length - 1]);
    if (!first || !last) return null;
    return {
      isTransfer: legs.length > 1,
      fromCity: first.fromStop.city,
      toCity: last.toStop.city,
      fromAddress: first.fromStop.address || "",
      toAddress: last.toStop.address || "",
      departDt: getStopDepartureDatetime(first.fromStop),
      arriveDt: getStopArrivalDatetime(last.toStop),
      hub: sel.itinerary.hub_city ?? (legs.length > 1 ? legs[0].to_city : null),
      layoverMinutes: sel.itinerary.layover_minutes ?? 0,
      totalDurationMinutes: sel.itinerary.total_duration_minutes ?? null,
      stopsSlice: null,
      legs,
    };
  }

  if (!sel.trip) return null;
  const tripStops = adjustTimes(dayjs(sel.trip.departure_date), sel.trip.route?.stops ?? []);
  const fromIdx = tripStops.findIndex((s) => cityMatchesSearch(s.city, sel.fromCity));
  const toIdx = tripStops.findIndex((s) => cityMatchesSearch(s.city, sel.toCity));
  if (fromIdx === -1 || toIdx === -1 || fromIdx >= toIdx) {
    return {
      isTransfer: false,
      fromCity: sel.fromCity,
      toCity: sel.toCity,
      fromAddress: "",
      toAddress: "",
      departDt: dayjs(sel.trip.departure_date),
      arriveDt: null,
      hub: null,
      layoverMinutes: 0,
      totalDurationMinutes: null,
      stopsSlice: null,
      legs: null,
    };
  }
  const fromStop = tripStops[fromIdx];
  const toStop = tripStops[toIdx];
  return {
    isTransfer: false,
    fromCity: fromStop.city,
    toCity: toStop.city,
    fromAddress: fromStop.address || "",
    toAddress: toStop.address || "",
    departDt: getStopDepartureDatetime(fromStop),
    arriveDt: getStopArrivalDatetime(toStop),
    hub: null,
    layoverMinutes: 0,
    totalDurationMinutes: getSegmentDurationMinutes(fromStop, toStop),
    stopsSlice: tripStops.slice(fromIdx, toIdx + 1),
    legs: null,
  };
};

export const StopTimelineRow = ({ stop, isLast, t }) => {
  const stopTime = isLast ? getStopArrivalDatetime(stop) : getStopDepartureDatetime(stop);
  if (!stopTime) return null;
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Box minWidth="75px">
          <Typography variant="h5" sx={{ fontWeight: "bold", color: "primary.main", mr: 1 }}>
            {stopTime.format("HH:mm")}
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#000", mr: 1 }}>
            {stopTime.format("DD.MM.YYYY")}
          </Typography>
        </Box>
        <CircleIcon sx={{ fontSize: 12, color: "primary.main", mr: 1 }} />
        <Box>
          <Typography variant="body1" sx={{ fontWeight: "bold", color: "#111827" }}>
            {getCityDisplay(stop?.city)}{" "}
            {stop.by_transfer && (
              <Typography variant="caption" component="span" sx={{ fontSize: 10, ml: 1 }}>
                ({t("checkout.transfer")})
              </Typography>
            )}
          </Typography>
          {stop?.address && (
            <Typography variant="body2" sx={{ color: "#6B7280" }}>
              <MapAddressLink city={getCityDisplay(stop.city)} address={stop.address} />
            </Typography>
          )}
        </Box>
      </Box>
      {!isLast && (
        <Box sx={{ borderLeft: `2px dashed ${getRuntimeBRAND().primary}`, height: "20px", ml: "12px", mb: 1 }} />
      )}
    </Box>
  );
};
